<template>
  <component :is="component"
             v-bind="isMobile ? bottomSheetProps : inputModalProps"
             @onCloseButtonClicked="onClose"
             @input="onMobileClose"
             @onCancelClicked="copyLink"
             @onOkClicked="sendEmailLink">
    <template :slot="slotName">
      <VcLoader v-if="loading"
                :class="{'desktop': !isMobile}"/>

      <div class="copy-link-container"
           :class="{'copy-link-container--hidden': loading}">
        <div class="description">
          <span>{{ $t('common.action_editor.copy_link.content') }}</span>
          {{ ' ' }}
          <span class="filename">{{ filenameWithoutExtension || title }}</span>
        </div>

        <div class="link-container">
          <span class="link-container__link">{{awsLink || publicLink || link}}</span>
          <VcButton v-if="!isTwoActions"
                    :label="$t('common.action_editor.copy_link.copy')"
                    :loading="inProgress"
                    @click="copyLink">
            <template #prepend>
              <VcIcon color="#ffffff" size="16">icon-copy</VcIcon>
            </template>
          </VcButton>
        </div>
        <div class="notice-container">
          <VcAlert type="info"
                   :body-text="infoAlertText"/>
          <span v-if="isPaymentableType"
            class="expiration-notice">
              <VcIcon class="icon-Lock2" color="#478FFF" size="16"/>
                {{$t('payment.share_file.copy_link_dialog.notice')}}
          </span>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <VcModalFooter v-if="isTwoActions"
        direction="horizontal"
        :buttons="dialogButtons"
        @onCopyClick="copyLink"
        @onSendEmailLinkClick="sendEmailLink"
        />
    </template>
  </component>
</template>

<script>
import {docuformsService} from "@/modules/common/services";
import {linkService} from "@/modules/payments/services";
import {showSuccess, showError} from "@/modules/common/services/frontageToastService.js";
import {mapActions, mapGetters} from "vuex";

const PAYMENT_SOURCE_TYPES = ['invoice', 'estimate', 'receipt'];
const SERVICE_SOURCE_TYPES = ['service', 'staff', 'category'];
const SOURCE_TYPES = ['document', 'package', 'invoice', 'estimate', 'receipt', 'service', 'staff', 'category'];

export default {
  name: "CopyLink",
  props: {
    uid: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    link: {
      type: String,
      required: true
    },
    source: {
      type: String,
      default: "document",
      validator: prop => SOURCE_TYPES.includes(prop)
    }
  },
  data() {
    return {
      loading: true,
      inProgress: false,
      fileStorageType: null,
      awsLink: null,
      publicLink: null,
      privacy: null,
      showBottomSheet: true
    }
  },
  async created() {
    if (this.isPaymentableType) {
      this.publicLink = await linkService.generatePublicUrl(this.link);
    } else if (this.isDocument) {
      const template = await docuformsService.getTemplate(this.uid);
      this.fileStorageType = template.file_storage_type;

      if (this.fileStorageType === 'AWS-S3') {
        await this.setAwsLink(template.metadata.file_location);
        this.privacy = template.file_privacy;
      }
    }
    this.loading = false;
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapGetters('appMarketModule', ['externalApp', 'externalApps']),
    component() {
      return this.isMobile ? 'VcInputBottomSheet' : 'VcInputModal';
    },
    slotName() {
      return this.isMobile ? 'default' : 'content';
    },
    inputModalProps() {
      return {
        showDialog: true,
        closeButton: true,
        showFooter: this.isTwoActions,
        title: this.$t('common.action_editor.copy_link.title'),
        size: 'md',
      }
    },
    bottomSheetProps() {
      return {
        value: this.showBottomSheet,
        footer: this.isTwoActions,
        title: this.$t('common.action_editor.copy_link.title'),
        okButtonLabel: this.$t("livesite.link_dialog.send"),
        cancelButtonLabel: this.$t('common.action_editor.copy_link.copy'),
      }
    },
    filenameWithoutExtension() {
      return this.title.split('.').slice(0,-1).join('.');
    },
    isDocument() {
      return this.source === 'document';
    },
    isPaymentableType() {
      return PAYMENT_SOURCE_TYPES.includes(this.source);
    },
    infoAlertText() {
      return this.$t(`common.action_editor.copy_link.alerts.${this.source}`)
    },
    expiredLinkNotice() {
      return this.$t('payment.share_file.copy_link_dialog.notice');
    },
    isServiceType() {
      return SERVICE_SOURCE_TYPES.includes(this.source);
    },
    dialogButtons() {
      return [
      { label: this.$t('common.action_editor.copy_link.copy') ,event: "onCopyClick", color: 'white'},
      { label: this.$t("livesite.link_dialog.send"), event: "onSendEmailLinkClick", color: "secondary"}
      ];
    },
    isTwoActions() {
      return this.isServiceType || this.source === 'package';
    }
  },
  methods: {
    ...mapActions('appMarketModule', ['loadExternalApps']),
    async replaceFileHost(link) {
      !this.externalApps.length && await this.loadExternalApps();
      const originalHost = this.externalApp('documents')?.api_uri;
      const newHost = `https://${window.app_config.frontage_location}/apps/documents`;
      return originalHost ? link.replace(originalHost, newHost) : link;
    },
    async setAwsLink(fileLocation) {
      this.awsLink = this.$hasFeature('auth_documents') ? await this.replaceFileHost(fileLocation) : fileLocation;
    },
    async sendEmailLink() {
      this.$frontageApi.send('send-link-email')
      this.isMobile ? this.onMobileClose() : this.onClose();
    },
    async copyLink() {
      if (this.isDocument && this.fileStorageType === 'AWS-S3' && this.privacy === 'Private') {
        this.inProgress = true;
        const template = await docuformsService.setAsPublic(this.uid);
        await this.setAwsLink(template.metadata.file_location);
      }

      navigator.clipboard.writeText(this.awsLink || this.publicLink || this.link).then(
        () => showSuccess(this.$t('common.action_editor.copy_link.success')),
        () => showError(this.$t('common.action_editor.copy_link.error'))
      );

      this.isMobile ? this.onMobileClose() : this.onClose();
    },
    onClose() {
      this.$emit('close');
    },
    onMobileClose() {
      setTimeout(() => this.$emit('close'), 100);
      this.showBottomSheet = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.copy-link-container {

  &--hidden {
    visibility: hidden;
  }

  .description {
    font-size: 15px;
    margin-bottom: var(--size-value4);

    .filename {
      font-weight: 600;
    }
  }

  .link-container {
    display: flex;
    border: 1px solid var(--gray-lighten-1);
    border-radius: 6px;
    margin-bottom: var(--size-value4);
    padding: var(--size-value1) var(--size-value3);
    align-items: center;

    &__link {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      color: var(--gray-darken-4);
      font-size: var(--font-size-small2);
      line-height: var(--size-value5);
      font-weight: var(--font-weight-medium2);
      user-select: none;
    }

    .VcButton {
      margin-inline-start: var(--size-value5);
      margin-inline-end: -6px;
      margin-top: 2px;
      margin-bottom: 2px;

      .v-icon {
        margin-inline-end: var(--size-value2);
      }
    }
  }
}

.v-progress-circular {
  position: absolute;
  margin: auto;
  left: 0;
  right: 0;

  &.desktop {
    top: 0;
    bottom: 0;
  }
}
.notice-container {
  margin-bottom: 10px;
}
.expiration-notice {
  display: flex;
  margin-top: 10px;
  transform: rotate(0.016deg);
  gap: 8px;
  font-size: 13px;
  line-height: 20px;
}
</style>
